import { useEffect, useState } from "react";
import { api } from "@/api/client";

export interface SitzungsZahlen {
  /** Gäste, die sich angemeldet haben. Bei Solo-Offsites: ein Gast = ein Team. */
  angemeldet: number;
  /** Gäste, die ihren Prompt abgeschickt haben. */
  eingereicht: number;
  /** Angemeldete Bühnen-Plätze (Einmal-Code mit Bühnen-Rolle). */
  buehne: number;
  /** Angemeldete aus dem Publikum (Hintergrund-Codes). */
  publikum: number;
}

/**
 * Wie viele Gäste angemeldet sind und wie viele abgeschickt haben.
 *
 * Drei Stellen brauchen dieselbe Zahl: die Moderationsleiste, die Folie mit dem
 * QR-Code und das Teilnehmer-Handy. Deshalb liegt der Poll hier — und hinter
 * `GET /runde`, das beide Rollen lesen dürfen.
 *
 * Auf welche Besuchergruppe sich die Zahlen beziehen, entscheidet das Backend
 * (`phase.rundeSeit`): Teams aus früheren Durchläufen liegen weiter in der
 * Tabelle, zählen aber nicht mit.
 *
 * Der Zähler greift schon vor dem Prompten — `ParticipantPage` tritt dem
 * (idempotenten) Solo-Team direkt beim Öffnen bei, nicht erst in der
 * Arbeitsphase.
 *
 * Beigabe, nicht Grundlage: Fällt der Endpunkt aus, bleibt der Wert `null` und
 * die Sitzung läuft weiter.
 */
export function useSitzungsZahlen(
  enabled: boolean,
  token: string | null,
  intervalMs = 5000,
): SitzungsZahlen | null {
  const [zahlen, setZahlen] = useState<SitzungsZahlen | null>(null);

  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;

    const tick = async () => {
      try {
        const r = await api.runde.get(token);
        if (cancelled) return;
        setZahlen({
          angemeldet: r.angemeldet,
          eingereicht: r.eingereicht,
          buehne: r.buehne ?? 0,
          publikum: r.publikum ?? 0,
        });
      } catch {
        /* Zähler sind Beigabe — ohne sie moderiert man weiter. */
      }
    };

    tick();
    const id = window.setInterval(tick, intervalMs);
    return () => {
      cancelled = true;
      window.clearInterval(id);
    };
  }, [enabled, token, intervalMs]);

  return zahlen;
}
